import _round from 'lodash/round';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { LightningBoltIcon } from '@/components/icons/lightning-bolt';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useLastAutomationChannel } from '@/hooks/use-last-automation-channel';
import { cn } from '@/utils/classnames';
import { isWithinLast24Hours, toDate } from '@/utils/date-util';

import { PerformanceStatusBanner } from '../performance-status-banner';
import { BUDGET_THRESHOLD_PERCENTAGE } from './constants';
import { useAutomationMessages } from './use-automation-messages';
function AutomationStatusBanner() {
  const { setBudgetAutomationBannerColor } = useOpeningContext();
  // `useLastAutomationChannel` kicks off its sibling fetch before suspending,
  // so call it first to keep the channels-list query in parallel with the
  // funnel-details API.
  const channel = useLastAutomationChannel();
  const { opening, hiringGoal, analyticsData } = useOpeningDetails();
  const isCurrentHiringGoal = hiringGoal?.is_current ?? false;
  const { getAutomationMessages } = useAutomationMessages();
  const { openEditTargetsDialog } = useEditTargetsContext();

  const lastAutomationReason = channel?.deactivation_reason ?? null;
  const isActive = !channel?.deactivated_at;
  const lastAutomationTriggerAt = toDate(
    channel?.automation_triggered_at ?? null,
  );
  const bannerColor = isActive ? 'blue' : 'rose';

  /* Open edit targets dialog on click */
  const handleEditTargets = useCallback(() => {
    openEditTargetsDialog({
      targets: {
        conversion: analyticsData?.conversion_stats?.expected ?? null,
        budget: analyticsData?.budget_stats?.expected ?? null,
        cpa: analyticsData?.cpa_stats?.expected ?? null,
      },
      spendStats: analyticsData?.spend_stats,
    });
  }, [openEditTargetsDialog, analyticsData]);

  /* Check if the spend has breached the 80% threshold of the budget, and is less than the budget */
  const { hasSpendBreachedThreshold, spendPercentage } = useMemo(() => {
    const budget = analyticsData?.budget_stats?.expected ?? 0;
    const spend = analyticsData?.spend_stats?.total ?? 0;

    if (!budget || !spend)
      return { hasSpendBreachedThreshold: false, spendPercentage: undefined };

    const hasSpendBreachedThreshold =
      spend < budget && spend > budget * BUDGET_THRESHOLD_PERCENTAGE;
    const spendPercentage = hasSpendBreachedThreshold
      ? _round((spend / budget) * 100, 1)
      : undefined;

    return { hasSpendBreachedThreshold, spendPercentage };
  }, [analyticsData]);

  /* Set the banner color for budget automation banner. This is used to determine the color of the budget card in the analytics section.
   * Show the banner only if following conditions are met:
    - The hiring goal is current AND
    - The spend has breached the threshold OR
    - The last automation reason is 'target_budget_met' AND has triggered in the last 24 hours
   */
  useEffect(() => {
    if (
      isCurrentHiringGoal &&
      (hasSpendBreachedThreshold ||
        (lastAutomationReason === 'target_budget_met' &&
          lastAutomationTriggerAt &&
          isWithinLast24Hours(lastAutomationTriggerAt)))
    ) {
      setBudgetAutomationBannerColor(bannerColor);
    } else {
      setBudgetAutomationBannerColor(undefined);
    }
  }, [
    hasSpendBreachedThreshold,
    bannerColor,
    setBudgetAutomationBannerColor,
    lastAutomationReason,
    lastAutomationTriggerAt,
    isCurrentHiringGoal,
  ]);

  /* Get automation messages */
  const automationCopy = useMemo(() => {
    // If the spend has breached the 80% threshold, we should show the breached banner. This is a custom reason for the banner to show when the budget is breached. Other reasons are from the backend.
    const automationReason =
      !lastAutomationReason && hasSpendBreachedThreshold
        ? 'target_budget_breached'
        : lastAutomationReason;

    const copy = getAutomationMessages(
      automationReason,
      lastAutomationTriggerAt,
      spendPercentage,
    );

    if (!copy) return null;

    const title = isActive ? copy.activeTitle : copy.pausedTitle;
    const description = isActive
      ? copy.activeDescription
      : copy.pausedDescription;

    return {
      title,
      description,
    };
  }, [
    hasSpendBreachedThreshold,
    lastAutomationReason,
    getAutomationMessages,
    lastAutomationTriggerAt,
    spendPercentage,
    isActive,
  ]);

  /* Button logic */
  const button = useMemo(() => {
    if (
      lastAutomationReason === 'hiring_goal_ended' &&
      !isActive &&
      hiringGoal?.url
    ) {
      return (
        <Button variant="secondary" asChild>
          <Link to={hiringGoal.url} target="_blank">
            <FormattedMessage
              defaultMessage="Set new hiring goal"
              description="CTA when automation ended the old goal: open Hire to create a new one"
            />
          </Link>
        </Button>
      );
    }

    if (lastAutomationReason === 'target_budget_met' && !isActive) {
      return (
        <Button variant="secondary" onClick={handleEditTargets}>
          <FormattedMessage
            defaultMessage="Update Budget"
            description="CTA when spend target was hit: open edit targets for new budget"
          />
        </Button>
      );
    }

    return null;
  }, [lastAutomationReason, isActive, hiringGoal?.url, handleEditTargets]);

  /* For past hiring goal we should not show funnel/budget automation messages. We should show the hiring goal status banner instead. <PerformanceStatusBanner /> */
  if (!isCurrentHiringGoal && lastAutomationReason !== 'hiring_goal_ended') {
    return <PerformanceStatusBanner />;
  }

  /* If the automation was not triggered in the last 24 hours, we should not show the banner. */
  if (
    !hasSpendBreachedThreshold &&
    (!lastAutomationTriggerAt || !isWithinLast24Hours(lastAutomationTriggerAt))
  ) {
    return null;
  }

  /* For past hiring goal we should not show hiring automation message if there is an active hiring goal present. */
  if (
    lastAutomationReason === 'hiring_goal_ended' &&
    !isActive &&
    opening.hasActiveHiringGoal
  ) {
    return null;
  }

  /* If there is no automation copy, we should not show the banner. */
  if (!automationCopy) {
    return null;
  }

  return (
    <Banner
      color={bannerColor}
      title={automationCopy.title}
      description={automationCopy.description}
      button={button}
      icon={
        <LightningBoltIcon
          className={cn('h-4 w-4', isActive ? 'text-fx-blue' : 'text-fx-rose')}
        />
      }
    />
  );
}

export { AutomationStatusBanner };
